import { useState } from "react";

import { AddEmployeeProtectMedicalNotePopup } from "@/Common/Components/AddEmployeePopup/AddEmployeeProtectMedicalNotePopup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Guid } from "@/Common/Models/Guid";
import { IPatientFileAllowlist } from "@/Common/Models/Patient/IPatientFileAllowlist";
import { Person } from "@/Common/Models/Person/Person";
import { IconButton } from "@/DesignSystem";

import styles from "@/Patient/Components/Profile/ProtectPatientChartSection.module.scss";

import DeleteIcon from "@/Common/_images/RBAC_DeleteIcon.svg";

interface ProtectPatientFileSectionProps {
    model: IPatientFileAllowlist;
    editable: boolean;
    onMembersChange: (membersToAdd: Guid[] | null, membersToDelete: Guid[] | null) => void;
}

function ProtectPatientFileSection(props: Readonly<ProtectPatientFileSectionProps>): JSX.Element {
    const { getText } = useTranslation();
    const [showAddEmployeesPopup, setShowAddEmployeesPopup] = useState(false);

    const allowlistMembersId = props.model?.MemberIds ?? [];

    const onAddMembers = (memberIds: Guid[]): void => {
        const newMembers = memberIds.filter(p => !allowlistMembersId.includes(p));
        props.onMembersChange(newMembers, null);
    };

    const onRemove = (memberId: Guid): void => {
        props.onMembersChange(null, [memberId]);
    };

    const onRemoveAll = (): void => {
        props.onMembersChange(null, allowlistMembersId);
    };

    return (
        <>
            {showAddEmployeesPopup && (
                <AddEmployeeProtectMedicalNotePopup
                    contactFacetIds={allowlistMembersId}
                    onSave={contactFacetIds => {
                        setShowAddEmployeesPopup(false);
                        onAddMembers(contactFacetIds);
                    }}
                    onClose={() => setShowAddEmployeesPopup(false)}
                />
            )}
            <div className={styles.protectPatientChartContainer} data-cy="protect-patient-file-section">
                <div className={styles.header}>
                    <h3 className={styles.sectionTitle}>{getText("Allowlist_ProtectPatientChartLabel")}</h3>
                    <button className={styles.addUserLink} onClick={() => setShowAddEmployeesPopup(true)}>[+] {getText("Allowlist_AddAUserLabel")}</button>
                </div>
                {allowlistMembersId.any() && (
                    <div className={styles.messageContainer}>
                        <p className={styles.protectPatientChartMessageLabel}>{getText("Allowlist_ProtectPatientChartMessageOne")}</p>
                        <p>{getText("Allowlist_ProtectPatientChartMessageTwo")}</p>
                    </div>
                )}
                <div className={styles.body}>
                    {allowlistMembersId.any() && (
                        <>
                            <div className={styles.header}>
                                <h4 className={styles.sectionTitle}>{getText("Allowlist_IndividualUsersLabel")}</h4>
                                {props.editable && (
                                    <button
                                        className={styles.removeAllUsersLabel}
                                        onClick={() => onRemoveAll()}
                                        data-cy="remove-all-employees"
                                    >
                                        {getText("Allowlist_RemoveAllUsersLabel")}
                                    </button>
                                )}
                            </div>
                            <div className={styles.listContainer}>
                                {allowlistMembersId.map(contactFacetId => cacheStore.GetClinicians(true).find(employee => employee.ContactFacetId === contactFacetId))
                                    .sortByProperty(employee => `${employee.LastName}${employee.FirstName}`).map((person: Person) => (
                                        <div
                                            className={styles.listItem}
                                            key={`employee_${person.ContactFacetId}`}
                                            data-cy={`employee-${person.ContactFacetId}`}
                                        >
                                            <span>{person.prefix} {person.Name} {person.Licence && `(${person.Licence})`}</span>
                                            {props.editable && (
                                                <IconButton
                                                    onClick={() => onRemove(person.ContactFacetId)}
                                                    size="xs"
                                                    category="status-icon"
                                                    dataCy={`remove-employee-${person.ContactFacetId}`}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export { ProtectPatientFileSection };
